import type {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';

import { authStorage } from '@/api/auth/authStorage';

const API_BASE_URL =
    'https://okbq77mh8f.execute-api.us-east-1.amazonaws.com/Prod/api/v1';

const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
        const token = authStorage.getValidAccessToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error) {
        const isInvalidToken =
            result.error.status === 401 ||
            (result.error.status === 400 &&
                (result.error.data as any)?.detail === 'Invalid token') ||
            (result.error.data as any)?.detail === 'Token expire';

        if (isInvalidToken) {
            const refreshToken = authStorage.getValidRefreshToken();

            if (!refreshToken) {
                authStorage.clearTokens();
                toast.warning('Session expired. Please log in again.');
                window.location.href = '/sign-in';
                return result;
            }

            try {
                const refreshResult = await baseQuery(
                    {
                        url: '/auth/refresh',
                        method: 'POST',
                        body: { refreshToken },
                    },
                    api,
                    extraOptions,
                );

                if (refreshResult.data) {
                    const { accessToken, refreshToken: newRefreshToken } = (
                        refreshResult.data as any
                    ).data;

                    authStorage.setTokens(accessToken, newRefreshToken);

                    result = await baseQuery(args, api, extraOptions);
                } else {
                    authStorage.clearTokens();
                    toast.warning(
                        'Authentication failed. Please log in again.',
                    );
                    window.location.href = '/sign-in';
                }
            } catch {
                authStorage.clearTokens();
                toast.warning('Authentication failed. Please log in again.');
                window.location.href = '/sign-in';
            }
        }
    }

    return result;
};

export const api = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'User',
        'Post',
        'Posts',
        'SelfPost',
        'SelfPosts',
        'SelfStories',
        'FollowingUsersList',
        'PlatformUser',
        'Comments',
        'Notifications',
        'BusinessAd',
    ],
    endpoints: () => ({}),
});
